/**
 * 消息持久化 Composable：存入 localStorage 的逻辑，目前只在客户端执行
 *
 * 重要说明：
 * - 主要数据源：数据库（优先从数据库加载）
 * - localStorage 作用：仅作为离线缓存和网络失败的降级方案
 * - 保存策略：手动调用 saveMessages()，不在每次消息变化时自动保存
 *
 * 使用场景：
 * 1. 网络失败时显示缓存的历史消息
 * 2. 离线状态下临时查看之前的对话
 * 3. 提升用户体验，减少等待时间
 * import { useMessagePersistence } from "./useMessagePersistence";
 * const persistence = useMessagePersistence(messages);
 * persistence.clearStorageMessages()
 */

import type { Ref } from "vue";
import { StorageKey } from "~/enums";

// localStorage key（使用枚举）
const STORAGE_KEY = StorageKey.CHAT_MESSAGES;

// 最大存储消息数（防止localStorage溢出）
const MAX_MESSAGES = 200;

// 存储过期时间（7天）
const EXPIRY_DAYS = 7;

export interface StoredMessage {
  id: string;
  role: string;
  content: string;
  timestamp: number;
  thinkingSteps?: any[];
}

export interface StoredData {
  messages: StoredMessage[];
  savedAt: number;
  version: number;
}

/**
 * 消息持久化管理
 */
export function useMessagePersistence(messages: Ref<any[]>) {
  /**
   * 从 localStorage 加载消息
   */
  const loadMessages = (): any[] => {
    // 只在客户端执行
    if (process.server) {
      console.log("⚠️ 服务端跳过加载消息");
      return [];
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const data: StoredData = JSON.parse(stored);

      // 检查版本兼容性
      if (data.version !== 1) {
        console.warn("⚠️ 存储版本不兼容，清空旧数据");
        clearStorageMessages();
        return [];
      }

      // 检查是否过期
      const now = Date.now();
      const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (now - data.savedAt > expiryMs) {
        console.log("🗑️ 消息已过期，清空");
        clearStorageMessages();
        return [];
      }

      console.log(`📂 从本地存储加载了 ${data.messages.length} 条消息`);
      return data.messages;
    } catch (error) {
      console.error("❌ 加载消息失败:", error);
      return [];
    }
  };

  /**
   * 保存消息到 localStorage
   */
  const saveMessages = () => {
    // 只在客户端执行
    if (process.server) {
      return;
    }

    try {
      // 限制消息数量
      const messagesToSave = messages.value.slice(-MAX_MESSAGES);

      // 转换为可存储格式
      const storedMessages: StoredMessage[] = messagesToSave.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.createdAt || Date.now(),
        thinkingSteps: msg.thinkingSteps,
      }));

      const data: StoredData = {
        messages: storedMessages,
        savedAt: Date.now(),
        version: 1,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      console.log(`💾 已保存 ${storedMessages.length} 条消息到本地存储`);
    } catch (error) {
      // localStorage 满了或其他错误
      if (
        error instanceof DOMException &&
        error.name === "QuotaExceededError"
      ) {
        console.warn("⚠️ localStorage 已满，清理旧消息");
        clearStorageMessages();
      } else {
        console.error("❌ 保存消息失败:", error);
      }
    }
  };

  /**
   * 清空所有消息
   */
  const clearStorageMessages = () => {
    // 只在客户端执行
    if (process.server) {
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
    console.log("🗑️ 本地消息已清空");
  };

  /**
   * 获取存储使用情况
   */
  const getStorageInfo = () => {
    // 只在客户端执行
    if (process.server) {
      return { count: 0, size: 0 };
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return { count: 0, size: 0 };

      const size = new Blob([stored]).size;
      const data: StoredData = JSON.parse(stored);

      return {
        count: data.messages.length,
        size: size,
        sizeKB: (size / 1024).toFixed(2),
        savedAt: new Date(data.savedAt).toLocaleString("zh-CN"),
      };
    } catch {
      return { count: 0, size: 0 };
    }
  };

  return {
    loadMessages,
    saveMessages,
    clearStorageMessages,
    getStorageInfo,
  };
}
